/**
 * Custom Authentication Service
 *
 * Handles authentication with the custom login endpoint
 * Replaces Cognito Hosted-UI with direct API authentication
 * DO NOT DELETE DOMAIN HEADER - the backend uses it to identify the tenant and apply tenant-specific logic and IT WON'T RAISE CORS, Backend is prepared to accept that specific custom header
 */
import {API_ROUTES} from "../routes/api.tsx";
import {API_URL, ENVIROMENT} from "../config/env.ts";

interface Rol {
  rol: string;
  weight: number;
}

interface Person {
  id: number;
  document_number?: string | null;
  name?: string | null;
  treatment?: string | null;
  surname?: string | null;
  gender?: string | null; // Single character: 'F' (Femenino), 'M' (Masculino), 'O' (Otro)
  phone_number?: string | null;
  address?: string | null;
  birth_date?: string | null;
  estado?: boolean | null;
}

interface User {
  id: number;
  email: string;
  company_id: string;
  personas: Person[];
  roles: Rol[];
  estado: boolean;
}

interface Session_information {
  user: User;
}

interface GoogleLoginResponse {
  bearer_token: string;
  session_information: Session_information;
}

interface LoginResponse {
  bearer_token: string;
  session_information: User;
}

interface RegisterData {
  name: string;
  surname: string;
  document_number: string;
  email: string;
  address: string;
  phone_number: string;
  birth_date: string;
  gender: 'F' | 'M' | 'O';
  password: string;
  treatment?: string;
  turnstile_token: string;
}

interface UpdateProfileData {
  name: string;
  surname: string;
  document_number: string;
  email: string;
  address: string;
  phone_number: string;
  birth_date: string;
  gender: 'F' | 'M' | 'O';
  treatment?: string;
}

interface VerifyEmailData {
  email: string;
  code: string;
  turnstile_token?: string;
}

interface VerifyEmailResponse {
  message: string;
  success: boolean;
}

interface ResendVerificationData {
  email: string;
  turnstile_token: string;
}

interface ForgotPasswordResponse {
  message?: string;
}

interface ConfirmForgotPasswordData {
  code: string;
  email: string;
  password: string;
}

interface ConfirmForgotPasswordResponse {
  message?: string;
}

interface RegisterResponse {
  message?: string;
  // Add other response fields as needed
}

interface JWTPayload {
  email?: string;
  sub?: string;
  exp?: number;
  // Allow additional fields from the JWT payload
  [key: string]: string | number | boolean | undefined;
}

interface BackendErrorBody {
  error?: string;
  type?: string;
  code?: string;
}

/**
 * Parse a non-ok response from the backend, which uses the standard format:
 * { "error": "texto de error.", "type": "string", "code": "codigo_de_error" }
 *
 * Throws an Error whose message is "Error <status>: <error>".
 * When present, attaches the backend `code` to the Error object for
 * callers that need to branch on specific error codes (e.g. email_not_verified).
 */
async function throwBackendError(response: Response, fallback: string): Promise<never> {
  let body: BackendErrorBody = {};
  try {
    body = (await response.json()) as BackendErrorBody;
  } catch {
    // Response body is not JSON – keep empty object and use fallback
  }
  const message = body.error
    ? `Error ${response.status}: ${body.error}`
    : `Error ${response.status}: ${fallback}`;
  const err = new Error(message);
  if (body.code) {
    (err as Error & { code: string }).code = body.code;
  }
  throw err;
}

/**
 * Parse JWT token to extract payload
 * Note: This is a simple base64 decode - tokens should be validated on the backend
 */
function parseJWT(token: string): JWTPayload | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
}

/**
 * Set a Clinarya shared cookie with the standard attributes.
 * The cookie is readable across all subdomains of clinarya.com.
 * The value is URL-encoded.
 */
function setClinaryCookie(name: string, value: string, expires: Date): void {
  const parts = [
    `${name}=${value}`,
    `path=/`,
    `Domain=.clinarya.com`,
    `expires=${expires.toUTCString()}`,
    `SameSite=None`,
    `Secure`,
  ];
  document.cookie = parts.join('; ');
}

/**
 * Store login authentication data in shared cookies readable across all
 * clinarya.com subdomains.  Uses the JWT token's expiration when available,
 * falling back to 24 hours from now.
 */
function storeLoginCookies(bearerToken: string, sessionInformation: Session_information): void {
  let expires: Date;
  const payload = parseJWT(bearerToken);
  if (payload && typeof payload.exp === 'number') {
    expires = new Date(payload.exp * 1000);
  } else {
    expires = new Date(Date.now() + 24 * 60 * 60 * 1000);
  }
  setClinaryCookie('CLINARYA_TOKEN', bearerToken, expires);
  if (sessionInformation != null) {
    setClinaryCookie('CLINARYA_SESSION_INFORMATION', JSON.stringify(sessionInformation), expires);
  }
}

/**
 * Store login authentication data in shared cookies readable across all
 * clinarya.com subdomains.  Uses the JWT token's expiration when available,
 * falling back to 24 hours from now.
 */
function storeSessionCookies(sessionInformation: Session_information): void {
  const expires = new Date(Date.now() + 24 * 60 * 60 * 1000);

  if (sessionInformation != null) {
    setClinaryCookie('CLINARYA_SESSION_INFORMATION', JSON.stringify(sessionInformation), expires);
  }
}

/**
 * Ensures the CLINARYA_SUBDOMAIN cookie is set for pages reached via an
 * emailed link (e.g. confirm-forgot-password, verify-email).
 *
 * Priority:
 *  1. If the cookie already exists, return its decoded value.
 *  2. If not, read 'company-domain' from the provided search string, write
 *     the cookie (Domain=.clinarya.com, SameSite=None, Secure, 1-day expiry)
 *     and return the value.
 *  3. If neither is present, return null so the caller can show an error.
 *
 * In development mode, always returns 'local' (no cookie needed).
 *
 * @param search - window.location.search / location.search from the component
 */
export function ensureTenantCookieFromQuery(search: string): string | null {
  if (ENVIROMENT === 'development') {
    return 'local';
  }

  // 1. Cookie already present?
  const raw = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('CLINARYA_SUBDOMAIN='))
    ?.substring('CLINARYA_SUBDOMAIN='.length);
  if (raw) return decodeURIComponent(raw);

  // 2. Try company-domain query param
  const params = new URLSearchParams(search);
  const company = params.get('company-domain') ?? '';
  if (!company) {
    return null;
  }

  // Create the cookie so all subsequent service calls can read it
  const expires = new Date(Date.now() + 24 * 60 * 60 * 1000); // 1 day
  setClinaryCookie('CLINARYA_SUBDOMAIN', encodeURIComponent(company), expires);
  return company;
}

/**
 * Read and decode the CLINARYA_SUBDOMAIN cookie set by the tenant portal.
 * The cookie value is URL-encoded, so decodeURIComponent is applied.
 * Returns an empty string when the cookie is absent.
 */
export function getTenantSubdomain(): string {
  if (ENVIROMENT === 'development') {
    return 'local';
  }
  const raw = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('CLINARYA_SUBDOMAIN='))
    ?.substring('CLINARYA_SUBDOMAIN='.length);
  return raw ? decodeURIComponent(raw) : '';
}

/**
 * Return the tenant display name derived from the CLINARYA_SUBDOMAIN cookie,
 * with the first letter capitalised. Falls back to 'Clinarya' when the cookie
 * is absent so that both the login and verification pages always show a
 * consistent brand name.
 */
export function getTenantDisplayName(): string {
  const subdomain = getTenantSubdomain();
  if (!subdomain) return 'Clinarya';
  return subdomain.charAt(0).toUpperCase() + subdomain.slice(1);
}

/**
 * Get stored user data
 */
export function getUserData(): User | null {
  const raw = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith('CLINARYA_SESSION_INFORMATION='))
      ?.substring('CLINARYA_SESSION_INFORMATION='.length);

  if (!raw) return null;

  try {
    const decoded = decodeURIComponent(raw);
    // La cookie se guarda como JSON de Session_information; adaptamos al tipo User si es necesario.
    const sessionInfo = JSON.parse(decoded) as Session_information;
    return sessionInfo?.user ?? null;
  } catch (error) {
    console.error('Failed to parse CLINARYA_SESSION_INFORMATION cookie:', error);
    return null;
  }
}

/**
 * Login with username and password
 * @param username - Email or phone number
 * @param password - User password
 * @param turnstileToken
 */
export async function login(username: string, password: string, turnstileToken: string): Promise<LoginResponse> {
  const domain = getTenantSubdomain();

  try {
    const response = await fetch(API_URL + API_ROUTES.AUTH.LOGIN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'domain': domain
      },
      body: JSON.stringify({
        username,
        password,
        turnstile_token: turnstileToken,
      }),
    });

    if (!response.ok) {
      await throwBackendError(response, 'Login failed');
    }

    const data = await response.json();

    // Store bearer token and session information in shared cookies so they are
    // accessible from all clinarya.com subdomains after the redirect.
    if (data.bearer_token) {
      storeLoginCookies(data.bearer_token, data.session_information);
    }

    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

/**
 * Login with Google OAuth
 * @param code - OAuth authorization code
 */
export async function loginWithGoogle(code: string): Promise<GoogleLoginResponse> {
  const domain = getTenantSubdomain();

  try {
    const response = await fetch(API_URL + API_ROUTES.AUTH.LOGIN_GOOGLE, {

      method: 'POST',
      headers: {
        'domain': domain,
        'code': code
      },
    });

    if (!response.ok) {
       await throwBackendError(response, 'Google login failed');
    }

     const data: GoogleLoginResponse = await response.json();

    // Store bearer token and session information in shared cookies so they are
    // accessible from all clinarya.com subdomains after the redirect.
    if (data.bearer_token) {
      storeLoginCookies(data.bearer_token, data.session_information);
    }

    return data;
  } catch (error) {
    console.error('Google login error:', error);
    throw error;
  }
}

/**
 * Check whether a usable Clinarya session token is present.
 *
 * Deliberately narrower than `isAuthenticated()`: it only asks whether a
 * CLINARYA_TOKEN exists and has not expired. It does NOT require a completed
 * profile (`personas.length > 0`) nor a match between the user's company and
 * the current tenant.
 *
 * Those two extra checks are what make `isAuthenticated()` unusable in the
 * Google callback: a brand-new Google user holds a perfectly valid session but
 * has no persona yet, so `isAuthenticated()` would report `false`, the callback
 * would fall through and replay an already-consumed authorization code. Keep
 * the two functions separate.
 */
export function hasValidSessionToken(): boolean {
  const token = getAuthToken();

  if (!token) {
    return false;
  }

  const payload = parseJWT(token);
  if (!payload || !payload.exp) {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  return payload.exp > now;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const token = getAuthToken();
  const user_data = getUserData();

  if (user_data) {
    if (user_data.personas.length < 1) {
      return false;
    }
    if (user_data.company_id !== getTenantSubdomain()) {
      return false;
    }
  }
  else{
    return false;
  }

  if (!token) {
    return false;
  }

  // Parse token to check expiration
  const payload = parseJWT(token);
  if (!payload || !payload.exp) {
    return false;
  }

  // Check if token is expired
  const now = Math.floor(Date.now() / 1000);
  return payload.exp > now;
}

/**
 * Get authentication token for API requests
 */
export function getAuthToken(): string | null {
  const raw = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith('CLINARYA_TOKEN='))
      ?.substring('CLINARYA_TOKEN='.length);

  return raw ? decodeURIComponent(raw) : null;
}

/**
 * Register a new user
 * @param data - User registration data
 */
export async function register(data: RegisterData): Promise<RegisterResponse> {
  const domain = getTenantSubdomain();

  try {
    const response = await fetch(API_URL + API_ROUTES.AUTH.REGISTER, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'domain': domain,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await throwBackendError(response, 'Registration failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
}

/**
 * Update user profile (for completing profile after Google signup)
 * @param data - User profile data
 */
export async function updateUserProfile(data: UpdateProfileData): Promise<RegisterResponse> {
  const token = getAuthToken();
  const domain = getTenantSubdomain();

  if (!token) {
    throw new Error('No authentication token found');
  }

  try {
    const response = await fetch(API_URL + API_ROUTES.PERSONS.PERSONA, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'domain': domain,

      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await throwBackendError(response, 'Profile update failed');
    }
    
    // Leer el body una sola vez y usarlo para guardar cookies y devolver la respuesta
    const json = await response.json();

    // Si el backend devuelve `session_information`, usarlo tal cual.
    // Si devuelve únicamente `user`, envolverlo en la estructura esperada.
    if (json) {
      if (json.session_information) {
        storeSessionCookies(json.session_information as Session_information);
      } else if (json.user) {
        storeSessionCookies({ user: json.user } as Session_information);
      }
    }

    return json;
  } catch (error) {
    console.error('Profile update error:', error);
    throw error;
  }
}

/**
 * Verify email with code
 * @param data - Email and verification code
 */
export async function verifyEmail(data: VerifyEmailData): Promise<VerifyEmailResponse> {
  const domain = getTenantSubdomain();

  try {
    const response = await fetch(API_URL + API_ROUTES.AUTH.VERIFY_EMAIL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'domain': domain,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await throwBackendError(response, 'Email verification failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Email verification error:', error);
    throw error;
  }
}

/**
 * Send forgot password email
 * @param email - Email address to send reset instructions to
 * @param turnstileToken
 */
export async function forgotPassword(email: string, turnstileToken: string): Promise<ForgotPasswordResponse> {
  const domain = getTenantSubdomain();

  try {
    const response = await fetch(API_URL + API_ROUTES.AUTH.SEND_FORGOT_PASSWORD_EMAIL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'domain': domain,
      },
      body: JSON.stringify({ email, turnstile_token: turnstileToken }),
    });

    if (!response.ok) {
      await throwBackendError(response, 'Forgot password failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
}

/**
 * Resend verification code
 * @param data - Email to resend code to
 */
export async function resendVerificationCode(data: ResendVerificationData): Promise<RegisterResponse> {
  try {
    const domain = getTenantSubdomain();

    const response = await fetch(API_URL + API_ROUTES.AUTH.RESEND_VERIFICATION, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'domain': domain,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await throwBackendError(response, 'Resend verification failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Resend verification error:', error);
    throw error;
  }
}

/**
 * Confirm forgot password with code and new password
 * @param data - Code, email and new password
 */
export async function confirmForgotPassword(data: ConfirmForgotPasswordData): Promise<ConfirmForgotPasswordResponse> {
  const domain = getTenantSubdomain();

  try {
    const response = await fetch(API_URL + API_ROUTES.AUTH.CONFIRM_FORGOT_PASSWORD, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'domain': domain,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await throwBackendError(response, 'Confirm forgot password failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Confirm forgot password error:', error);
    throw error;
  }
}

// Export types for use in components
// (ensureTenantCookieFromQuery, getTenantSubdomain, getTenantDisplayName are exported inline above)
export type {
  RegisterData,
  RegisterResponse,
  UpdateProfileData,
  User,
  Person,
  GoogleLoginResponse,
  VerifyEmailData,
  VerifyEmailResponse,
  ResendVerificationData,
  ForgotPasswordResponse,
  ConfirmForgotPasswordData,
  ConfirmForgotPasswordResponse,
};
